// src/pages/happinessreport/components/FiltersBar.tsx

import React from 'react';
import {
  FormControl, InputLabel, Select, OutlinedInput, MenuItem, Checkbox, ListItemText, ListSubheader, Button, IconButton, CircularProgress, Box
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { ContainerSelectFilters, ContainerFilters } from '../styles';
import { representationAgeRangeOptions, representationSexOptions } from '../../../services/utils/representationOptions';
import { useTranslation } from 'react-i18next';
import {Col} from 'react-bootstrap';
import { getLoadingFilterOptions, selectionIndicatorSlotSx } from '../../filters/filterSelection';

type Props = {
  controller: any;
};

const FiltersBar: React.FC<Props> = ({ controller }) => {
  const { t } = useTranslation('insights');
  const sexLabels = [t('sexOptions.male'), t('sexOptions.female'), t('sexOptions.preferNot')];
  const ageLabels = [t('ageOptions.under24'), t('ageOptions.from25to30'), t('ageOptions.from31to35'), t('ageOptions.from36to40'), t('ageOptions.from41to45'), t('ageOptions.from46to50'), t('ageOptions.from51to55'), t('ageOptions.from56to60'), t('ageOptions.over61')];
  // Destructure from controller (com checagens)
  const {
    dateValue,
    setDateValue,
    datePickerIsOpened,
    setDatePickerIsOpened,
    clientTimezone,
    loading,
    representationSelectedOptions,
    setRepresentationSelectedOptions,
    selectGroupOptions,
    selectedGroupOptions,
    setSelectedGroups,
    handleNextMonth,
    handlePreviousMonth,
    loadFilteredData
  } = controller;

  // estados para controlar abertura dos selects
  const [repSelectOpen, setRepSelectOpen] = React.useState(false);
  const [groupSelectOpen, setGroupSelectOpen] = React.useState(false);

  // utilitários para checar se há algum filtro ativo
  const hasRepValue = Boolean(representationSelectedOptions && representationSelectedOptions.length > 0);
  const hasGroupValue = Boolean(selectedGroupOptions && selectedGroupOptions.length > 0);
  const loadingRepresentationOptions = getLoadingFilterOptions(representationSelectedOptions, loading);
  const loadingGroupOptions = getLoadingFilterOptions(selectedGroupOptions, loading);

  // utility para obter year/month da dateValue (YYYY-MM)
  const getYearMonthFromDateValue = (dv?: string | null) => {
    if (!dv || typeof dv !== 'string') return { year: undefined, month: undefined };
    const [year, month] = dv.split('-');
    return { year, month };
  };

  const handleCloseSelect = () => {
    setTimeout(() => {
      if (document?.activeElement && typeof (document.activeElement as HTMLElement).blur === 'function') {
        (document.activeElement as HTMLElement).blur();
      }
    }, 0);
  };

  const handleChangeRepresentation = (event: any) => {
    const { target: { value } } = event;
    if (loading || typeof setRepresentationSelectedOptions !== 'function') return;
    const nextOptions = typeof value === 'string' ? value.split(',') : [...value];
    setRepresentationSelectedOptions(nextOptions);
    const { year, month } = getYearMonthFromDateValue(dateValue);
    void controller.loadFilteredData(year, month, {
      representationSelectedOptions: nextOptions,
      selectedGroupOptions: selectedGroupOptions ?? [],
    });
  };

  const handleChangeGroup = (event: any) => {
    const { target: { value } } = event;
    if (loading || typeof setSelectedGroups !== 'function') return;
    const nextOptions = typeof value === 'string' ? value.split(',') : [...value];
    setSelectedGroups(nextOptions);
    const { year, month } = getYearMonthFromDateValue(dateValue);
    void controller.loadFilteredData(year, month, {
      representationSelectedOptions: representationSelectedOptions ?? [],
      selectedGroupOptions: nextOptions,
    });
  };

  return (
    <ContainerSelectFilters>
      <Col>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
          <div style={{ display: 'flex', gap: '.5em' }}>
            <IconButton 
              sx={{ 
                height: '100%', 
                background:'#FFFFFF', 
                borderRadius:'.2em', 
                minHeight: '56px',  
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                '&.Mui-disabled': {
                  cursor: 'wait',
                  backgroundColor: "#f0f0f0", 
                  color: "#a0a0a0", 
                },

              }} 
              disabled={loading} onClick={handlePreviousMonth}
            >
              <ChevronLeftIcon sx={{ fontSize: 35 }} />
            </IconButton>

            <IconButton 
              sx={{ 
                height: '100%', 
                background:'#FFFFFF', 
                borderRadius:'.2em', 
                minHeight: '56px',  
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                '&.Mui-disabled': {
                    cursor: 'wait',
                    backgroundColor: "#f0f0f0", 
                    color: "#a0a0a0", 
                    borderColor:"#c9c9c9",
                  },
              }} 
              disabled={loading} 
              onClick={handleNextMonth}
            >
              <ChevronRightIcon sx={{ fontSize: 35 }} />
            </IconButton>
          </div>

          <FormControl disabled={loading} sx={{ background: '#FFF', minHeight: '52px', borderRadius: '6px', width: '100%',
            '@media (max-width:694px)': {
              width: '100%',
              fontSize: '14px', 
            },  

            '@media (min-width:695px) and (max-width: 1200px)': {
              width: '50%',
              fontSize: '14px',
            },  

            '@media (min-width:1201px) and (max-width: 1440px)': {
              width: '34%',
              fontSize: '14px',
            },  

            '@media (min-width:1441px)': {
              width: '34%',
              fontSize: '14px',
            },

            '&.Mui-disabled':{
              cursor: 'wait',
              backgroundColor: "#f0f0f0",
            },
              
            '& .MuiOutlinedInput-root.Mui-disabled':{
              cursor: 'wait',
              backgroundColor: "#f0f0f0", 
              color: "#a0a0a0", 
              "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#c9c9c9",
              },
            },

          }}>
            <DatePicker
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent', // Cor da borda
                  borderRadius:'6px',
                  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)', 

                },
                '&:focus-visible .MuiOutlinedInput-notchedOutline': {
                  outline: 'transparent', // Isso removerá o outline
                },
                '&.Mui-focused': {
                  fontWeight: 'bold',
                  border: 'transparent', // Isso removerá o outline
                },
                '&.MuiInputLabel-outlined': { // Estilo padrão
                  fontWeight: 'bold',
                  
                },
                '& .MuiOutlinedInput-root': {
                  cursor: 'pointer'
                },
                '& .MuiInputBase-input': {
                  cursor: 'pointer'
                },

                '& .MuiInputBase-input.Mui-disabled': {
                  cursor: 'wait'
                },
                
                '&.Mui-disabled':{
                    cursor: 'wait',
                    backgroundColor: "#f0f0f0",
                  },
              
                '& .MuiOutlinedInput-root.Mui-disabled':{
                  cursor: 'wait',
                  backgroundColor: "#f0f0f0", 
                  color: "#a0a0a0", 
                  "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#c9c9c9",
                  },
                },
                                                          
              }}
              open={datePickerIsOpened}
              onClose={() => setDatePickerIsOpened(false)}
              disabled={loading}
              slotProps={{
                textField: {
                  onClick: () => {
                    if (!loading)
                      setDatePickerIsOpened(true)
                  },
                },
                field: {
                  readOnly: true
                },
                layout: {
                  sx: {
                    [`.MuiPickersYear-root`]: {
                      flexBasis: '100%',
                      width: '100%',

                    },
                    [`.MuiPickersYear-yearButton`]: {
                      fontSize: '1.65em',
                      margin: '.5em 0',
                      lineHeight: '0',
                      width: '45%',
                      height: '2.5em',
                      borderRadius: '2em',
                      
                    },
                    
                    [`.MuiPickersMonth-monthButton::after`]:{
                      content: '"."',
                      position: 'absolute',

                    },
                  },
                },
              }}
              format='MMMM/YYYY'
              timezone={clientTimezone}
              views={['month', 'year']}
              value={dateValue ? dayjs(dateValue) : null}
              onAccept={async (selectedDate: any) => {
                try {
                  const formatted = dayjs(selectedDate).tz(clientTimezone).format('YYYY-MM');
                  if (typeof setDateValue === 'function') setDateValue(formatted);
                  if (typeof controller.loadDataByDate === 'function') {
                    await controller.loadDataByDate(formatted);
                  } else if (typeof controller.loadFilteredData === 'function') {
                    await controller.loadFilteredData(formatted.split('-')[0], formatted.split('-')[1]);
                  }
                } catch (err) {
                  console.error('Erro onAccept datepicker:', err);
                } finally {
                  if (typeof setDatePickerIsOpened === 'function') setDatePickerIsOpened(false);
                }
              }}
            />
          </FormControl>
        </div>

      </Col>
      

      <ContainerFilters>
        <FormControl sx={{ width: '100%', background: '#FFF', minHeight: '52px', borderRadius: '6px' }}>
          <InputLabel id="multiple-select-representation-label" shrink={hasRepValue || repSelectOpen}>{t('filterRepresentation')}</InputLabel>
          <Select
            labelId="multiple-select-representation-label"
            id="multiple-select-representation"
            multiple
            value={representationSelectedOptions ?? []}
            onChange={handleChangeRepresentation}
            onOpen={() => { setRepSelectOpen(true); }}
            onClose={() => { setRepSelectOpen(false); handleCloseSelect(); }}
            IconComponent={FilterListIcon}
            input={<OutlinedInput label={t('filterRepresentation')}
              color='primary'
              notched={hasRepValue || repSelectOpen}
              sx={{
                  color: '#3C4043', //cor do texto dentro do input

                  '&.Mui-disabled':{
                      cursor: 'wait',
                      backgroundColor: "#f0f0f0",
                  },
              
                  '& .MuiOutlinedInput-root.Mui-disabled':{
                      cursor: 'wait',
                      backgroundColor: "#f0f0f0", 
                      color: "#a0a0a0", 
                      "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#c9c9c9",
                      },
                  },

                  '& .MuiSelect-select.Mui-disabled':{
                      cursor: 'wait',
                      color: "#a0a0a0",
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (hasRepValue || repSelectOpen) ? '#c9c9c9' : 'transparent', // Cor da borda
                      borderRadius: '6px',
                      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',

                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor:  '#c9c9c9' , // Cor da borda ao passar o mouse
                  },
                  '& .MuiSelect-iconOpen': {
                      transform: 'none !important',
                  },

                  '@media (max-width:601px)': {
                      width: '100%',
                      fontSize: '14px', // Reduz a largura para 70% em telas maiores
                  },
              }}

            />}
            renderValue={(selected) => (selected as string[]).map(value => { const sexIndex = representationSexOptions.indexOf(value); const ageIndex = representationAgeRangeOptions.indexOf(value); return sexIndex >= 0 ? sexLabels[sexIndex] : ageIndex >= 0 ? ageLabels[ageIndex] : value; }).join(', ')}
          >
            <ListSubheader>{t('sex')}</ListSubheader>
            {representationSexOptions.map((name: string, index: number) => (
              <MenuItem key={name} value={name} disabled={loading} sx={{ '&.Mui-disabled': { cursor: 'progress', opacity: 0.65 } }}>
                <Box sx={selectionIndicatorSlotSx}>
                  {loadingRepresentationOptions.has(name) ?
                    <CircularProgress size={18} sx={{ color: 'gray' }} /> :
                    <Checkbox checked={(representationSelectedOptions ?? []).indexOf(name) > -1} />}
                </Box>
                <ListItemText primary={sexLabels[index]} />
                <Button
                  sx={{ display: (representationSelectedOptions ?? []).indexOf(name) > -1 ? 'block' : 'none' }}
                  variant="outlined"
                  onClick={(event) => { event.stopPropagation(); handleChangeRepresentation({ target: { value: [name] } }); }}
                >{t('only')}</Button>
              </MenuItem>
            ))}

            <ListSubheader>{t('age')}</ListSubheader>
            {representationAgeRangeOptions.map((name: string, index: number) => (
              <MenuItem key={name} value={name} disabled={loading} sx={{ '&.Mui-disabled': { cursor: 'progress', opacity: 0.65 } }}>
                <Box sx={selectionIndicatorSlotSx}>
                  {loadingRepresentationOptions.has(name) ?
                    <CircularProgress size={18} sx={{ color: 'gray' }} /> :
                    <Checkbox checked={(representationSelectedOptions ?? []).indexOf(name) > -1} />}
                </Box>
                <ListItemText primary={ageLabels[index]} />
                <Button
                  sx={{ display: (representationSelectedOptions ?? []).indexOf(name) > -1 ? 'block' : 'none' }}
                  variant="outlined"
                  onClick={(event) => { event.stopPropagation(); handleChangeRepresentation({ target: { value: [name] } }); }}
                >{t('only')}</Button>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: '100%', background: '#FFF', minHeight: '52px', borderRadius: '6px' }}>
          <InputLabel id="multiple-select-group-label" shrink={hasGroupValue || groupSelectOpen}>{t('filterGroup')}</InputLabel>
          <Select
            labelId="multiple-select-group-label"
            id="multiple-select-group"
            multiple
             value={selectedGroupOptions ?? []}
            onChange={handleChangeGroup}
            onClose={() => { setGroupSelectOpen(false); handleCloseSelect(); }}
            onOpen={() => setGroupSelectOpen(true)}
            IconComponent={FilterListIcon}
            input={<OutlinedInput label={t('filterGroup')}
              color='primary'
              notched={hasGroupValue || groupSelectOpen}
              sx={{
                  color: '#3C4043', //cor do texto dentro do input

                  '&.Mui-disabled':{
                      cursor: 'wait',
                      backgroundColor: "#f0f0f0",
                  },
              
                  '& .MuiOutlinedInput-root.Mui-disabled':{
                      cursor: 'wait',
                      backgroundColor: "#f0f0f0", 
                      color: "#a0a0a0", 
                      "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#c9c9c9",
                      },
                  },

                  '& .MuiSelect-select.Mui-disabled':{
                      cursor: 'wait',
                      color: "#a0a0a0",
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (hasGroupValue || groupSelectOpen) ? '#c9c9c9' : 'transparent', // Cor da borda
                      borderRadius: '6px',
                      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',

                  },
                  '.MuiSelect-iconOpen': {
                      transform: 'none !important',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor:  '#c9c9c9', // Cor da borda ao passar o mouse
                  },

                  borderRadius: '6px',
                  border: 'none'


              }}

            />}
            renderValue={(selectedGroups) => (selectedGroups as string[])
              .map(g => (selectGroupOptions.find((option: any) => option.value == g) as any)?.label ?? '').join(', ')
            }
          >
            {selectGroupOptions.map((group: any) => (
              <MenuItem key={group.value} value={group.value} disabled={loading} sx={{ '&.Mui-disabled': { cursor: 'progress', opacity: 0.65 } }}>
                <Box sx={selectionIndicatorSlotSx}>
                  {loadingGroupOptions.has(group.value) ?
                    <CircularProgress size={18} sx={{ color: 'gray' }} /> :
                    <Checkbox checked={(selectedGroupOptions ?? []).indexOf(group.value) > -1} />}
                </Box>
                <ListItemText primary={group.label} />
                <Button
                  sx={{ display: (selectedGroupOptions ?? []).indexOf(group.value) > -1 ? 'block' : 'none' }}
                  variant="outlined"
                  onClick={(event) => { event.stopPropagation(); handleChangeGroup({ target: { value: [group.value] } }); }}
                >{t('only')}</Button>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </ContainerFilters>
    </ContainerSelectFilters>
  );
};

export default FiltersBar;
